import { useEffect, useState } from "react";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, KeyRound, RefreshCw, Copy, Check, Shield, AlertTriangle, BookOpen, Database } from "lucide-react";
import { toast } from "sonner";
import { Field, SelectField } from "@/pages/Equipes";

const EMPTY = { full_name: "", role: "joueur", phone: "", email: "", linked_player_id: "" };

export default function Utilisateurs() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [players, setPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [codeDialog, setCodeDialog] = useState(null); // { user, access_code }
  const [copied, setCopied] = useState(false);

  const load = () => Promise.all([
    api.get("/users").then((r) => setUsers(r.data)),
    api.get("/players").then((r) => setPlayers(r.data)).catch(() => setPlayers([])),
  ]);
  useEffect(() => { load(); }, []);

  const isAdmin = me?.role === "admin";

  const save = async () => {
    try {
      const payload = { ...form, linked_player_id: form.linked_player_id || null };
      const { data } = await api.post("/users", payload);
      setCodeDialog({ user: data.user, access_code: data.access_code });
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const regen = async (u) => {
    if (!window.confirm("Régénérer le code d'accès ? L'ancien sera invalidé.")) return;
    try {
      const { data } = await api.post(`/users/${u.id}/regenerate-code`);
      setCodeDialog({ user: u, access_code: data.access_code });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet utilisateur ?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const copyCode = () => {
    navigator.clipboard.writeText(codeDialog.access_code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const resetAllData = async () => {
    const confirm1 = window.prompt(
      "⚠️ ATTENTION : cette action va SUPPRIMER toutes les données du club (joueuses, transferts, finances, RH, membres, documents, calendrier, budget, stats, annonces).\n\nLes comptes utilisateurs sont conservés.\n\nPour confirmer, tapez exactement : RÉINITIALISER"
    );
    if (confirm1 !== "RÉINITIALISER") {
      if (confirm1 !== null) toast.error("Annulé — texte de confirmation incorrect.");
      return;
    }
    try {
      const { data } = await api.post("/admin/reset-data");
      const total = Object.values(data.deleted || {}).reduce((s, n) => s + n, 0);
      toast.success(`${total} enregistrement(s) supprimé(s). Le club peut commencer une saison vierge.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadFile = async (url, filename, mimeType) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: mimeType });
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
      toast.success("Téléchargement démarré");
    } catch (e) {
      toast.error("Échec du téléchargement");
    }
  };

  const downloadManual = () => downloadFile(
    "/admin/user-manual", "manuel_amalvbc.pdf", "application/pdf"
  );

  const downloadBackup = () => downloadFile(
    "/admin/backup",
    `amalvbc_backup_${new Date().toISOString().slice(0, 10)}.zip`,
    "application/zip"
  );

  return (
    <div data-testid="users-page">
      <PageHeader title="Utilisateurs & accès"
        subtitle="Gérez les codes d'accès personnalisés et les rôles">
        <Button onClick={downloadManual} variant="outline" data-testid="download-manual-button">
          <BookOpen className="h-4 w-4 mr-2" /> Manuel PDF
        </Button>
        {isAdmin && (
          <>
            <Button onClick={downloadBackup} variant="outline" data-testid="backup-button"
              className="text-emerald-700 border-emerald-200 hover:bg-emerald-50">
              <Database className="h-4 w-4 mr-2" /> Sauvegarde
            </Button>
            <Button onClick={resetAllData} variant="outline" data-testid="reset-data-button"
              className="text-red-600 border-red-200 hover:bg-red-50">
              <AlertTriangle className="h-4 w-4 mr-2" /> Réinitialiser
            </Button>
            <Button onClick={() => setOpen(true)} data-testid="add-user-button"
              className="bg-emerald-600 hover:bg-emerald-700">
              <Plus className="h-4 w-4 mr-2" /> Nouvel utilisateur
            </Button>
          </>
        )}
      </PageHeader>

      <div className="p-6 lg:p-8">
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full data-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                <th className="text-left p-4">Nom complet</th>
                <th className="text-left p-4">Rôle</th>
                <th className="text-left p-4">Contact</th>
                <th className="text-left p-4">Créé le</th>
                <th className="text-center p-4">Statut</th>
                {isAdmin && <th className="text-right p-4">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-slate-100">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm">
                        {u.full_name?.[0]}
                      </div>
                      <div className="font-semibold text-slate-900">{u.full_name}</div>
                      {u.id === me?.id && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Vous</Badge>}
                    </div>
                  </td>
                  <td className="p-4">
                    <Badge className="bg-slate-100 text-slate-700 border-slate-200 flex items-center gap-1 w-fit">
                      <Shield className="h-3 w-3" />
                      {ROLE_LABELS[u.role] || u.role}
                    </Badge>
                  </td>
                  <td className="p-4 text-xs text-slate-600">
                    <div>{u.phone || "—"}</div>
                    <div className="text-slate-400">{u.email || ""}</div>
                  </td>
                  <td className="p-4 text-sm text-slate-600">{formatDate(u.created_at)}</td>
                  <td className="p-4 text-center">
                    <Badge className={u.active
                      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                      : "bg-slate-200 text-slate-600 border-slate-300"}>
                      {u.active ? "Actif" : "Désactivé"}
                    </Badge>
                  </td>
                  {isAdmin && (
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => regen(u)}
                          data-testid={`regen-${u.id}`} className="h-8 text-xs">
                          <RefreshCw className="h-3 w-3 mr-1" /> Code
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toggleActive(u)}
                          data-testid={`toggle-active-${u.id}`} className="h-8 text-xs">
                          {u.active ? "Désactiver" : "Activer"}
                        </Button>
                        {u.id !== me?.id && (
                          <Button size="sm" variant="ghost" onClick={() => del(u.id)}
                            data-testid={`delete-user-${u.id}`}
                            className="text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create user dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">Nouvel utilisateur</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Field label="Nom complet" value={form.full_name}
                onChange={(v) => setForm({ ...form, full_name: v })} required testid="user-name" />
            </div>
            <SelectField label="Rôle" value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
              options={Object.entries(ROLE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
              testid="user-role" />
            <Field label="Téléphone" value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })} testid="user-phone" />
            <div className="col-span-2">
              <Field label="Email" type="email" value={form.email}
                onChange={(v) => setForm({ ...form, email: v })} testid="user-email" />
            </div>
            {(form.role === "joueur" || form.role === "parent") && (
              <div className="col-span-2">
                <SelectField label={form.role === "parent" ? "Joueuse rattachée (enfant)" : "Joueuse liée"}
                  value={form.linked_player_id || "__none__"}
                  onChange={(v) => setForm({ ...form, linked_player_id: v === "__none__" ? "" : v })}
                  options={[
                    { value: "__none__", label: "— Aucune —" },
                    ...players.map((p) => ({
                      value: p.id,
                      label: `${p.first_name} ${p.last_name} (${p.category}${p.jersey_number != null ? ` #${p.jersey_number}` : ""})`,
                    })),
                  ]}
                  testid="user-linked-player" />
                <div className="text-[10px] text-slate-500 mt-1">
                  Donne accès au portail joueuse (profil, calendrier équipe, stats personnelles).
                </div>
              </div>
            )}
          </div>
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md p-3">
            <KeyRound className="h-3.5 w-3.5 inline mr-1 text-emerald-600" />
            Un <strong>code d'accès personnel</strong> sera généré automatiquement.
            Il sera affiché <strong>une seule fois</strong> — pensez à le transmettre à l'utilisateur de manière sécurisée.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} data-testid="save-user-button" className="bg-emerald-600 hover:bg-emerald-700">
              Créer & générer le code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Code reveal dialog */}
      <Dialog open={!!codeDialog} onOpenChange={(v) => !v && setCodeDialog(null)}>
        <DialogContent className="max-w-md" data-testid="code-reveal-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl">Code d'accès généré</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600">
            Code pour <strong>{codeDialog?.user?.full_name}</strong> ({ROLE_LABELS[codeDialog?.user?.role]})
          </div>
          <div className="bg-emerald-50 border-2 border-emerald-200 border-dashed rounded-md p-6 text-center">
            <div className="font-mono font-bold text-2xl text-emerald-700 tracking-wider select-all" data-testid="generated-code">
              {codeDialog?.access_code}
            </div>
          </div>
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            ⚠️ Ce code ne sera plus jamais affiché. Notez-le ou transmettez-le maintenant.
          </div>
          <DialogFooter>
            <Button onClick={copyCode} variant="outline" data-testid="copy-code-button">
              {copied ? <><Check className="h-4 w-4 mr-2" /> Copié</> : <><Copy className="h-4 w-4 mr-2" /> Copier</>}
            </Button>
            <Button onClick={() => setCodeDialog(null)} className="bg-emerald-600 hover:bg-emerald-700">
              J'ai noté le code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
